import type { FC } from 'react';
import { IntlShape, MessageDescriptor } from 'react-intl';

import { CopyItem } from '@/components/valuePropList';
import genericMessages from '@/messages/messages';
import { CampaignConfig } from '@/store/campaignConfig/types';
import { settleWithin } from '@/utils/settleWithin';

import { AccountScreenConfig } from '../../Account/types';
import CampaignRedemption from '../../CampaignRedemption';
import { CampaignRedemptionScreenConfig } from '../../CampaignRedemption/types';
import AdditionalContent from '../../Components/AdditionalContent';
import { NextScreenParams, ScreenConfig } from '../../Context/types';
import { HandoffScreenConfig } from '../../Handoff/types';
import { PaymentScreenConfig } from '../../Payment/types';
import { selectStandardOfferTerms } from '../../utils/offerTerms';
import ControlScreenConfigs, { accountValueProps } from '../Control/Screens';
import partnerMessages from '../Partnerships/messages';
import { CAMPAIGN_FUNNEL_EVENTS, campaignEventProps } from './analytics';
import campaignMessages from './messages';
import { openPostRedemptionCta, resolvePostRedemptionCta } from './postRedemptionCta';
import { PageConfigContent, PageConfigSettings, resolveImageUrl, TrialInfo } from './types';

const DEFAULT_HEADER_TEMPLATE = '{Partner} is offering you {offer} of Calm Premium';

/**
 * How long the success CTA waits for its click event to flush before navigating anyway. Long
 * enough for a normal Amplitude round trip, short enough that a blocked or slow analytics endpoint
 * (ad blockers commonly block Amplitude) cannot stall the primary conversion action.
 */
const CTA_EVENT_FLUSH_TIMEOUT_MS = 1000;

/**
 * `formatMessage` throws if a descriptor has no `id`, even with `defaultMessage` set. These
 * ids are fixed and never match the real catalog, so `formatMessage` always falls back to
 * `defaultMessage` — admin-authored text renders verbatim with no translation lookup.
 */
function dynamicMessage(id: string, defaultMessage: string): MessageDescriptor {
	return { id, defaultMessage };
}

/**
 * Neutralizes ICU MessageFormat syntax so admin-authored text renders as plain text instead of
 * `formatMessage` erroring on a stray brace. formatjs only opens a quoted region when a `'` sits
 * immediately next to a `{`/`}`/`'`, so quoting each brace individually leaves adjacent quote
 * pairs touching (`{{` → `'{''{'`), which formatjs reads as one continuing quote instead of two
 * literal braces — each contiguous *run* of braces must be quoted as a single unit. Never use on
 * `header` — it intentionally keeps real `{Partner}`/`{offer}` interpolation.
 */
function escapeIcuLiteral(text: string): string {
	return text.replace(/'/g, "''").replace(/[{}]+/g, run => `'${run}'`);
}

function dynamicLiteralMessage(id: string, text: string): MessageDescriptor {
	return dynamicMessage(id, escapeIcuLiteral(text));
}

/**
 * Same as `escapeIcuLiteral`, but for text that DOES take named placeholders (`header`, which
 * admins can customize while keeping `{Partner}`/`{offer}` interpolation) — a stray `{`/`}`
 * anywhere disables every placeholder in the message, not just itself, since `formatMessage`
 * doesn't throw, it just returns the raw template uninterpolated. Keeps each allowed `{name}`
 * span verbatim and runs `escapeIcuLiteral` over everything between/around them, rather than
 * escaping stray braces individually (the same touching-quote collision `escapeIcuLiteral` above
 * had to be rewritten to avoid).
 */
function escapeIcuKeepingPlaceholders(text: string, placeholderNames: string[]): string {
	const allowed = new Set(placeholderNames);
	let escaped = '';
	let literalStart = 0;
	for (const match of text.matchAll(/\{[^{}]*\}/g)) {
		if (!allowed.has(match[0].slice(1, -1))) continue;
		escaped += escapeIcuLiteral(text.slice(literalStart, match.index)) + match[0];
		literalStart = match.index + match[0].length;
	}
	return escaped + escapeIcuLiteral(text.slice(literalStart));
}

/**
 * Each description line becomes one bullet's `header` (bold), not `body` — there's no separate
 * detail sentence in this data model. Falls back to `accountValueProps` whenever there's no real
 * content, not just when `descriptions` is missing: the wizard always saves exactly 3 slots,
 * padding any unfilled line with `''` rather than writing a shorter array, so an untouched
 * locale looks identical to a deliberately-cleared one — and there's no admin action today that
 * means "show nothing," so treating an all-blank array as "nothing yet" is the safer read.
 */
function buildUpsellBullets(content: PageConfigContent): CopyItem[] {
	const descriptions = Array.isArray(content.descriptions) ? content.descriptions : [];
	const bullets = descriptions
		.filter((line): line is string => Boolean(line))
		.map((line, index) => ({
			id: `campaign-description-${index}`,
			header: dynamicLiteralMessage(`campaign.page.description.${index}`, line),
		}));
	return bullets.length > 0 ? bullets : accountValueProps;
}

/**
 * `SisuAuthForm`/`Payment` render `heading` as
 * `typeof message === 'string' ? message : formatMessage(message, placeholders)` — always use
 * the `MessageDescriptor` branch here, since the default header template needs `{Partner}`/
 * `{offer}` interpolated.
 */
function buildHeadingMessage(rawTemplate: string, placeholders: Record<string, string | number>) {
	const template = escapeIcuKeepingPlaceholders(rawTemplate, Object.keys(placeholders));
	return { message: dynamicMessage('campaign.page.header', template), placeholders };
}

/** Resolves the trial-length clause through `formatMessage` so its plural form is a translator's call. */
function formatTrialPhrase(formatMessage: IntlShape['formatMessage'], trial: TrialInfo): string | null {
	if (!trial.duration) return null;
	const message =
		trial.unit === 'months' ? campaignMessages.trialPhraseMonths : campaignMessages.trialPhraseDays;
	return formatMessage(message, { length: trial.length });
}

/**
 * Plain-language offer description for the `{offer}` token in the header template (default or
 * admin-authored). Composed from a fixed set of translatable variants (mirroring the
 * `loginSignupTerms`/`_noTrial`/`_noTrial_noPromo` pattern in `Partnerships/messages.ts`) rather
 * than joined with a hardcoded "and" in JS, so word order and phrasing stay a translator's call
 * per locale instead of being fixed to English grammar. Always a real phrase, never empty, since
 * "{Partner} is offering you  of Calm Premium" would otherwise render with a dangling double
 * space for a no-trial, no-discount campaign.
 */
function offerSummary(
	formatMessage: IntlShape['formatMessage'],
	trial: TrialInfo,
	percentOff: number | null,
): string {
	const trialPhrase = formatTrialPhrase(formatMessage, trial);
	if (trialPhrase && percentOff) {
		return formatMessage(campaignMessages.offerTrialAndDiscount, {
			trialPhrase,
			discountPercent: percentOff,
		});
	}
	if (trialPhrase) return trialPhrase;
	if (percentOff) return formatMessage(campaignMessages.offerDiscountOnly, { discountPercent: percentOff });
	return formatMessage(campaignMessages.offerNone);
}

const CampaignAccountScreenConfig = (
	campaign: CampaignConfig,
	content: PageConfigContent,
	settings: PageConfigSettings,
	termsMessage: MessageDescriptor,
	offer: string,
): AccountScreenConfig => {
	const heroUrl = resolveImageUrl(settings.hero);
	return {
		...ControlScreenConfigs.account,
		showHeader: false,
		upsellHeader: () => ({ message: partnerMessages.valuePropHeader }),
		loginTerms: termsMessage,
		signupTerms: termsMessage,
		termsPrefix: content.footer_prefix || undefined,
		termsSuffix: content.footer_suffix || undefined,
		heading: () =>
			buildHeadingMessage(content.header || DEFAULT_HEADER_TEMPLATE, {
				Partner: campaign.partner.name,
				offer,
			}),
		subheading: content.subheader
			? dynamicLiteralMessage('campaign.page.subheader', content.subheader)
			: undefined,
		subheadingPosition: 'above',
		upsellBullets: buildUpsellBullets(content),
		contentBasedImage: heroUrl ? { src: heroUrl } : undefined,
		AdditionalContent: () => <AdditionalContent />,
		// Brand campaigns take no payment. They go to the redemption screen, which posts the code to
		// the same purchase endpoint the paid flow uses and only then shows the handoff.
		nextScreen: ({ routeToScreen }: NextScreenParams) =>
			routeToScreen(campaign.bypass_payment ? 'redeem' : 'payment'),
	};
};

const CampaignPaymentScreenConfig = (
	campaign: CampaignConfig,
	content: PageConfigContent,
	settings: PageConfigSettings,
	offer: string,
): PaymentScreenConfig => {
	return {
		...ControlScreenConfigs.payment,
		showHeader: false,
		heading: () =>
			buildHeadingMessage(content.header || DEFAULT_HEADER_TEMPLATE, {
				Partner: campaign.partner.name,
				offer,
			}),
		subHeading: content.payment_page_text
			? { message: dynamicLiteralMessage('campaign.page.payment_text', content.payment_page_text) }
			: undefined,
		contentBasedImage: settings.payment_page_image_url ? { src: settings.payment_page_image_url } : undefined,
		showToggle: false,
		userCanExit: false,
		// The campaign purchase endpoint only accepts a Stripe payment method, not PayPal.
		hidePaypal: true,
		// Runs after the purchase form's own generic Success event. A bypass (brand) campaign never
		// reaches this screen; its conversion fires from the redemption screen as conversion_type
		// 'redemption'. `purchaseEventProps` brings the partnership block with the redeemed code;
		// this config's `campaign` was captured before code entry.
		afterPurchaseSuccess: (logEvent, purchaseEventProps) => {
			logEvent({
				eventName: CAMPAIGN_FUNNEL_EVENTS.conversion,
				eventProps: { ...campaignEventProps(campaign), ...purchaseEventProps, conversion_type: 'purchase' },
			});
		},
	};
};

/**
 * The card-less (brand) campaign's purchase step: no payment, one redemption call, then the
 * handoff. The co-branded frame matches the handoff that follows it.
 */
const CampaignRedemptionScreenConfigFor = (settings: PageConfigSettings): CampaignRedemptionScreenConfig => ({
	component: CampaignRedemption as FC,
	name: 'redeem',
	// A function, deliberately. Context's routing effect sends a logged-in user without a valid
	// subscription to Payment from any screen whose nextScreen is not a function, and the payment
	// screen is exactly what a brand campaign must never show. The screen routes itself to the
	// handoff once the grant exists.
	nextScreen: () => {},
	showHeader: false,
	showBranding: true,
	backgroundKey: 'gray0',
	userCanExit: false,
	openAppCta: resolvePostRedemptionCta(settings),
});

/**
 * The post-redemption screen, shared by the paid path (Payment routes here) and the bypass path
 * (the redemption screen routes here once the grant exists). Co-branding, the success copy and
 * the CTA destination all come from page_config; everything unset falls back to the generic Calm
 * handoff.
 */
const CampaignHandoffScreenConfig = (
	campaign: CampaignConfig,
	content: PageConfigContent,
	settings: PageConfigSettings,
): HandoffScreenConfig => {
	const successText = content.success_text;
	const cta = resolvePostRedemptionCta(settings);
	const heroUrl = resolveImageUrl(settings.hero);
	return {
		...ControlScreenConfigs.handoff,
		// A function, not Control's null, for the same reason as the redemption screen: a card-less
		// completion can land here before the store's user has caught up with the grant, and a
		// logged-in free user on a screen without a function nextScreen is sent to Payment.
		nextScreen: () => {},
		appImage: heroUrl ? { src: heroUrl, alt: '' } : undefined,
		// No site header, same as the account and payment screens. The co-branded Branding row
		// (Calm + partner logo) takes its place. Without a header there is no exit icon either,
		// hence userCanExit false. The CTA below is the way out.
		showHeader: false,
		showBranding: true,
		userCanExit: false,
		// Only read by Branding, which picks the gradient Calm logo for a light background.
		backgroundKey: 'gray0',
		// On a phone the store badge under the heading is the handoff. A QR code to scan with the
		// phone the user is already holding is noise.
		hideQRCode: { mobile: true, desktop: false },
		heading: successText
			? () => dynamicLiteralMessage('campaign.page.success_text', successText)
			: ControlScreenConfigs.handoff.heading,
		CTA: {
			label: genericMessages.openCalmApp,
			primary: true,
			// Handoff's own click event is prefixed with the flow name, which for campaigns is
			// `campaign-{slug}`, one event name per campaign, and it hardcodes a Payment referrer
			// that is wrong for a bypass completion. The fixed-name event below replaces it.
			logsOwnClickEvent: true,
			onClick: async (_routerPush, { device, logEventAsync }) => {
				// Awaited rather than fire-and-forget. The navigation below is a hard page unload for
				// every https destination, and an event still in flight at that point is lost. Bounded,
				// so analytics that never answers cannot hold the button hostage.
				await settleWithin(
					logEventAsync({
						eventName: 'Campaign : Success : CTA : Clicked',
						eventProps: {
							campaign_id: campaign.id,
							campaign_slug: campaign.slug,
							url: cta.href,
							is_app_deeplink: cta.isAppDeeplink,
							is_configured: cta.isConfigured,
						},
					}),
					CTA_EVENT_FLUSH_TIMEOUT_MS,
				);
				// Awaited so Handoff's press guard stays held until the store fallback has fired on
				// a phone, not only until the deeplink was requested.
				await openPostRedemptionCta(cta, device);
			},
		},
	};
};

/**
 * The flow mounts the Stripe Elements provider around every screen, and the provider's default is
 * to start loading Stripe.js the moment it mounts. A card-less campaign never takes a payment, so
 * each of its screens passes an explicit null instead, and Stripe is never loaded on the page at
 * all rather than merely never shown.
 */
const withoutStripe = <T extends ScreenConfig>(config: T): T => ({ ...config, stripe: null });

export const generateCampaignScreenConfigs = (
	campaign: CampaignConfig,
	content: PageConfigContent,
	settings: PageConfigSettings,
	trial: TrialInfo,
	formatMessage: IntlShape['formatMessage'],
) => {
	const termsMessage = selectStandardOfferTerms(Boolean(trial.duration), Boolean(campaign.percent_off));
	const offer = offerSummary(formatMessage, trial, campaign.percent_off);

	const screens = {
		account: CampaignAccountScreenConfig(campaign, content, settings, termsMessage, offer),
		payment: CampaignPaymentScreenConfig(campaign, content, settings, offer),
		redeem: CampaignRedemptionScreenConfigFor(settings),
		handoff: CampaignHandoffScreenConfig(campaign, content, settings),
		// Campaigns never customize this -- it exists so routeToScreen('error') (the
		// missing-purchase-code case in Context's routing effect) has somewhere to go.
		// Without it, screens.error is undefined and SimplifiedSignup renders nothing at
		// all for an ActiveScreen with no component.
		error: ControlScreenConfigs.error,
	};
	if (!campaign.bypass_payment) return screens;
	// Payment stays in the map so a stray routeToScreen('payment') still renders a screen rather
	// than nothing, but nothing in a brand campaign's flow routes there.
	return {
		account: withoutStripe(screens.account),
		payment: withoutStripe(screens.payment),
		redeem: withoutStripe(screens.redeem),
		handoff: withoutStripe(screens.handoff),
		error: withoutStripe(screens.error),
	};
};
